import { Phone } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RouteSelect from "./RouteSelect";
import AddChannelModal from "../modals/AddChannelModal";
import ChannelAddedModal from "../modals/ChannelAddelModal";
import ChannelPendingModal from "../modals/ChannelPendingModal";
import AddToSlackModal from "../modals/AddUserToSlack";
import {
  getForwardNumberOptions,
  resolveForwardNumberPhone,
  resolveForwardNumberSelectValue,
} from "../../utils/forwardNumberChannels";

const getChannelsFromStorage = () =>
  JSON.parse(localStorage.getItem("organization"))?.channels?.L || [];

const ForwardNumberUserField = ({
  value = "",
  onChange,
  labelClassName = "text-sm font-medium",
  descriptionClassName = "text-[12px] text-gray-500 mb-2",
  className = "mb-5",
}) => {
  const [channels, setChannels] = useState(getChannelsFromStorage);
  const [openAddChannelModal, setOpenAddChannelModal] = useState(false);
  const [openChannelAddedModal, setOpenChannelAddedModal] = useState(false);
  const [openChannelPendingModal, setOpenChannelPendingModal] = useState(false);
  const [addToSlackModal, setOpenAddToSlackModal] = useState(false);
  const [channelId, setChannelId] = useState(null);
  const onChannelAddedRef = useRef(null);

  const setChannelsAndPersist = useCallback((next) => {
    setChannels(next);
    const org = JSON.parse(localStorage.getItem("organization"));
    if (org) {
      org.channels = { L: next };
      localStorage.setItem("organization", JSON.stringify(org));
    }
  }, []);

  useEffect(() => {
    setChannels(getChannelsFromStorage());
  }, [value]);

  const destinationOptions = useMemo(
    () => getForwardNumberOptions(channels),
    [channels]
  );

  const destinationValue = useMemo(
    () => resolveForwardNumberSelectValue(value, channels, destinationOptions),
    [value, channels, destinationOptions]
  );

  const handleDestinationChange = useCallback(
    (selected) => {
      const phone = resolveForwardNumberPhone(selected, channels);
      onChange(phone || "");
    },
    [channels, onChange]
  );

  const callChannelAddedAndClear = useCallback((newChannelValue) => {
    if (onChannelAddedRef.current) {
      onChannelAddedRef.current(newChannelValue);
      onChannelAddedRef.current = null;
    }
  }, []);

  const openAddForwardingNumberModal = useCallback(() => {
    onChannelAddedRef.current = handleDestinationChange;
    setOpenAddChannelModal(true);
  }, [handleDestinationChange]);

  return (
    <>
      <div className={className}>
        <div className="flex items-center gap-1.5 mb-1">
          <Phone className="w-4 h-4 text-gray-500" />
          <label className={labelClassName}>Forwarding Number</label>
        </div>
        <p className={descriptionClassName}>
          Default number used when this user places outbound calls via forwarding device
        </p>
        <RouteSelect
          value={destinationValue}
          onChange={(selected) => {
            if (!selected) {
              onChange("");
              return;
            }
            handleDestinationChange(selected);
          }}
          options={destinationOptions}
          placeholder="No forwarding number assigned"
          addOption={{
            label: "Add Forwarding Number",
            onClick: openAddForwardingNumberModal,
          }}
        />
      </div>

      <ChannelAddedModal
        setChannels={setChannelsAndPersist}
        open={openChannelAddedModal}
        setOpen={setOpenChannelAddedModal}
      />
      <ChannelPendingModal
        open={openChannelPendingModal}
        setOpen={setOpenChannelPendingModal}
      />
      <AddToSlackModal
        open={addToSlackModal}
        setOpen={setOpenAddToSlackModal}
        channel_id={channelId}
        setChannelId={setChannelId}
      />
      <AddChannelModal
        open={openAddChannelModal}
        setOpen={setOpenAddChannelModal}
        channels={channels}
        setChannels={setChannelsAndPersist}
        channelAddedModal={openChannelAddedModal}
        setOpenChannelAddedModal={setOpenChannelAddedModal}
        channelPendingModal={openChannelPendingModal}
        setOpenChannelPendingModal={setOpenChannelPendingModal}
        addToSlackModal={addToSlackModal}
        setOpenAddToSlackModal={setOpenAddToSlackModal}
        setChannelId={setChannelId}
        initialChannelType="forward_number"
        callChannelAddedAndClear={callChannelAddedAndClear}
      />
    </>
  );
};

export default ForwardNumberUserField;
